// The activity dashboard: every National Accounts customer with their spend
// and tenure, filterable by customer and by franchisee. Every role lands
// here. Admins additionally get to the monthly import from the toolbar.
//
// For a clinic owner this is My Activity: the server scopes every figure
// to lines attributed to their own clinics and lists only the customers
// those clinics have worked, so the page reads as what the accounts have
// produced for them. The wording follows: revenue, not spend.
//
// Spend figures come from the monthly document imports; nothing on this
// screen is entered by hand.

import { useCallback, useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { getActivityOverview } from '../api.js'
import { useAuth } from '../auth.jsx'
import { ACTION } from '../../../shared/permissions.js'
import {
  Button,
  ButtonLink,
  EmptyState,
  Kpi,
  KpiRow,
  Notice,
  Panel,
  Pill,
  Select,
  Table,
  Tbody,
  Td,
  Th,
  Thead,
  Tr,
} from '../components/ui/index.js'
import { customerStatusTone } from '../tones/customers.js'
import {
  statusLabel,
  formatMoney,
  formatCount,
  formatDate,
  formatTenure,
  monthLabel,
} from '../vocab.js'

const EMPTY_FILTERS = { customerId: '', franchiseeId: '' }

export default function Activity() {
  const { can } = useAuth()
  const canImport = can(ACTION.ACTIVITY_IMPORT)
  const mine = !can(ACTION.ACTIVITY_READ_ALL)
  const [filters, setFilters] = useState(EMPTY_FILTERS)
  const [data, setData] = useState(null)
  const [loadError, setLoadError] = useState(null)

  const load = useCallback(async () => {
    try {
      const result = await getActivityOverview({
        customerId: filters.customerId,
        franchiseeId: filters.franchiseeId,
      })
      setData(result)
      setLoadError(null)
    } catch (caught) {
      setLoadError(caught.message)
    }
  }, [filters.customerId, filters.franchiseeId])

  useEffect(() => {
    load()
  }, [load])

  function change(field, value) {
    setFilters((current) => ({ ...current, [field]: value }))
  }

  if (loadError) {
    return <Notice tone="bad">{loadError}</Notice>
  }
  if (!data) return <div className="center-note">Loading</div>

  const isFiltered = Object.values(filters).some(Boolean)
  const customers = data.customers

  const franchiseeName = filters.franchiseeId
    ? data.franchisees.find((franchisee) => franchisee.id === filters.franchiseeId)?.name
    : null
  // The server nests FTFS figures (customer bills) only for a viewer who may
  // read them; their presence is what switches the page to both sources.
  const bills = data.totals.ftfs ?? null

  return (
    <>
      <div className="toolbar">
        <Select
          value={filters.customerId}
          onChange={(customerId) => change('customerId', customerId)}
          ariaLabel="Filter by customer"
          style={{ width: '16rem' }}
          options={[
            { value: '', label: 'All customers' },
            ...data.customers.map((customer) => ({
              value: customer.id,
              label: customer.name,
            })),
          ]}
        />
        <Select
          value={filters.franchiseeId}
          onChange={(franchiseeId) => change('franchiseeId', franchiseeId)}
          ariaLabel="Filter by franchisee"
          style={{ width: '16rem' }}
          options={[
            { value: '', label: 'All franchisees' },
            ...data.franchisees.map((franchisee) => ({
              value: franchisee.id,
              label: franchisee.name,
            })),
          ]}
        />
        {isFiltered ? (
          <Button variant="ghost" onClick={() => setFilters(EMPTY_FILTERS)}>
            Clear
          </Button>
        ) : null}
        <span className="spacer" />
        {canImport ? (
          <ButtonLink variant="primary" to="/activity/imports">
            Import data
          </ButtonLink>
        ) : null}
      </div>

      {bills ? (
        <KpiRow>
          <Kpi lead label="FTFS revenue this year" value={formatMoney(bills.ytdSpend)} />
          <Kpi label="FTFS lifetime revenue" value={formatMoney(bills.spend)} />
          <Kpi label="Owner revenue this year" value={formatMoney(data.totals.ytdSpend)} />
          <Kpi label="Owner lifetime revenue" value={formatMoney(data.totals.spend)} />
          <Kpi label="Tests performed" value={formatCount(data.totals.tests) ?? '0'} />
          <Kpi label="Customers with activity" value={String(data.totals.customersServed)} />
        </KpiRow>
      ) : (
        <KpiRow>
          <Kpi
            lead
            label={mine ? 'Your revenue this year' : 'Spend this year'}
            value={formatMoney(data.totals.ytdSpend)}
          />
          <Kpi
            label={mine ? 'Lifetime revenue' : 'Total spend'}
            value={formatMoney(data.totals.spend)}
          />
          <Kpi label="Tests performed" value={formatCount(data.totals.tests) ?? '0'} />
          <Kpi
            label={mine ? 'Customers served' : 'Customers with activity'}
            value={String(data.totals.customersServed)}
          />
        </KpiRow>
      )}

      <Panel
        title="Customers"
        support={
          franchiseeName
            ? `${customers.length} shown, figures scoped to work by ${franchiseeName}`
            : `${customers.length} shown`
        }
        flush
      >
        {customers.length === 0 ? (
          <EmptyState
            title={
              isFiltered
                ? 'Nothing matches those filters'
                : mine
                  ? 'Nothing yet'
                  : 'No customers yet'
            }
          >
            {isFiltered
              ? 'Widen the filters.'
              : mine
                ? 'Customers appear here once a monthly import records work by your clinics.'
                : 'Customers appear here as they are added or arrive through a monthly import.'}
          </EmptyState>
        ) : (
          <Table>
            <Thead>
              <Tr>
                <Th>Customer</Th>
                {mine ? null : <Th>Status</Th>}
                <Th>Customer for</Th>
                {bills ? (
                  <>
                    <Th numeric>FTFS this year</Th>
                    <Th numeric>FTFS lifetime</Th>
                    <Th numeric>Owner this year</Th>
                    <Th numeric>Owner lifetime</Th>
                  </>
                ) : (
                  <>
                    <Th numeric>{mine ? 'Revenue this year' : 'Spend this year'}</Th>
                    <Th numeric>{mine ? 'Lifetime revenue' : 'Lifetime spend'}</Th>
                  </>
                )}
                <Th numeric>Lifetime tests</Th>
                <Th numeric>{mine ? 'Your clinics' : 'Franchisees'}</Th>
                <Th>{mine ? 'Last work' : 'Last activity'}</Th>
              </Tr>
            </Thead>
            <Tbody>
              {customers.map((customer) => (
                <Tr key={customer.id}>
                  <Td>
                    <Link to={`/activity/customers/${customer.id}`}>{customer.name}</Link>
                    {customer.createdByImport ? (
                      <div className="ui-cell-meta">Created by import</div>
                    ) : null}
                  </Td>
                  {mine ? null : (
                    <Td>
                      <Pill tone={customerStatusTone[customer.status]}>
                        {statusLabel(customer.status)}
                      </Pill>
                    </Td>
                  )}
                  <Td>
                    {customer.customerSince ? (
                      <>
                        {formatTenure(customer.customerSince)}
                        <div className="ui-cell-meta">{formatDate(customer.customerSince)}</div>
                      </>
                    ) : (
                      <span className="muted">Not set</span>
                    )}
                  </Td>
                  {bills ? (
                    <>
                      <Td numeric>{formatMoney(customer.ftfs.ytdSpend)}</Td>
                      <Td numeric>{formatMoney(customer.ftfs.lifetimeSpend)}</Td>
                    </>
                  ) : null}
                  <Td numeric>{formatMoney(customer.ytdSpend)}</Td>
                  <Td numeric>{formatMoney(customer.lifetimeSpend)}</Td>
                  <Td numeric>{formatCount(customer.lifetimeTests) ?? '0'}</Td>
                  <Td numeric>{formatCount(customer.franchiseeCount) ?? '0'}</Td>
                  <Td>
                    {customer.lastActivityMonth ? (
                      monthLabel(customer.lastActivityMonth)
                    ) : (
                      <span className="muted">None recorded</span>
                    )}
                  </Td>
                </Tr>
              ))}
            </Tbody>
          </Table>
        )}
      </Panel>
    </>
  )
}
